import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Comunicaciones.
 *
 * ## Los seis endpoints NO cambian
 *
 * Los mismos que pedía `page-communications.js`, con los mismos parámetros y
 * los mismos cuerpos. Lo vigila `test_comunicaciones_pantalla.py`.
 *
 * ## El séptimo es prestado, y es a propósito
 *
 * `GET /notifications/recipients/search` vive en el router de Notificaciones,
 * no en el de Comunicaciones. Se usa desde aquí para la «Selección manual»,
 * que en el vanilla pedía esto:
 *
 *     <input placeholder="IDs separados por coma (ej: 3,5,8)" />
 *
 * Es decir, había que saberse el número de fila de cada persona en la base.
 * La pantalla hermana resuelve lo mismo con un buscador por nombre desde
 * 2026, y ese endpoint exige exactamente el mismo permiso
 * (`require_permission("notificaciones","manage")`), así que consumirlo no
 * abre ninguna puerta nueva ni cambia ningún contrato.
 *
 * ## Dos consultas independientes, no un `Promise.all`
 *
 * El vanilla cargaba las opciones de audiencia y el historial con
 * `Promise.all` y un `catch` que sustituía la pantalla entera. Demostrado en
 * el navegador el 2026-09-15 haciendo fallar **solo** el historial: se llevaba
 * por delante el compositor, que no lo necesitaba para nada.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Los tres canales válidos del servidor (`_VALID_CHANNELS`). */
export type Canal = 'feed' | 'email' | 'notif';

/** Los ocho tipos de audiencia (`distribution_service.AUDIENCE_LABELS`). */
export type TipoDeAudiencia =
  | 'todos_activos' | 'por_area' | 'por_rol' | 'por_empresa'
  | 'por_sede' | 'por_cargo' | 'por_etiqueta' | 'seleccion_manual';

export type EstadoComunicacion =
  | 'borrador' | 'programada' | 'enviando' | 'enviada' | 'error';

export interface OpcionCatalogo { id: number; name: string }
export interface OpcionRol { key: string; name: string }

/** Lo que devuelve `/communications/audience-options`. */
export interface OpcionesDeAudiencia {
  types: Array<{ key: TipoDeAudiencia; label: string }>;
  areas: OpcionCatalogo[];
  companies: OpcionCatalogo[];
  sedes: OpcionCatalogo[];
  positions: OpcionCatalogo[];
  roles: OpcionRol[];
  tags: string[];
}

/** Los parámetros de audiencia. El servidor los recibe como un dict libre;
 *  aquí se enumeran para que el compilador avise si alguien inventa una clave
 *  que `resolve_audience` no lee. */
export interface ParametrosDeAudiencia {
  area_id?: string | null;
  role?: string | null;
  company_id?: string | null;
  sede_id?: string | null;
  position_id?: string | null;
  tag?: string | null;
  user_ids?: number[];
}

export interface Comunicacion {
  id: number;
  title: string;
  status: EstadoComunicacion;
  channels: Canal[];
  audience_type: TipoDeAudiencia;
  /** La etiqueta legible, ya resuelta por el servidor
   *  (`AUDIENCE_LABELS`). El vanilla la pintaba tal cual y estaba bien; lo que
   *  NO estaba bien era la pantalla hermana, que enseñaba la clave cruda. */
  audience_label: string;
  recipient_count: number;
  metrics: { emailed?: number; feed?: number; notif?: number };
  feed_post_id?: number | null;
  notif_id?: number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  created_at?: string | null;
}

export interface MuestraDeAudiencia {
  count: number;
  /** El servidor manda **doce como máximo** (`communications.py:70`). Ese
   *  límite importa para el texto: con 300 destinatarios la muestra no es la
   *  lista, y decir «y N más» exige saber que está recortada. */
  sample: Array<{ id: number; full_name: string }>;
}

export interface PersonaBuscada {
  id: number;
  full_name: string;
  area?: string | null;
  email?: string | null;
}

export interface RespuestaDeEnvio {
  id: number;
  status: 'programada' | 'enviando';
  scheduled_at?: string;
}

export interface CuerpoDeEnvio {
  title: string;
  body: string;
  is_html: boolean;
  channels: Canal[];
  audience_type: TipoDeAudiencia;
  audience_params: ParametrosDeAudiencia;
  schedule_at: string | null;
}

/** Cuántas del historial se piden. Era 30 en el vanilla y se conserva. */
export const POR_PAGINA = 30;

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  historial: ['comunicaciones', 'historial'] as const,
  opciones: ['comunicaciones', 'opciones-de-audiencia'] as const,
  destinatarios: (q: string) => ['comunicaciones', 'destinatarios', q] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

/** Los catálogos del selector de audiencia.
 *
 *  `staleTime` alto porque son catálogos: áreas, empresas, sedes y cargos no
 *  cambian mientras alguien escribe un comunicado. */
export function useOpcionesDeAudiencia() {
  return useQuery({
    queryKey: claves.opciones,
    queryFn: () => api.get<OpcionesDeAudiencia>('/communications/audience-options'),
    staleTime: 10 * 60_000,
  });
}

export function useHistorial() {
  return useQuery({
    queryKey: claves.historial,
    queryFn: () => api.get<Comunicacion[]>('/communications/', { limit: POR_PAGINA }),
  });
}

/** Busca colaboradores por nombre para la selección manual.
 *
 *  `enabled` solo cuando el tipo de audiencia lo necesita: sin esto la consulta
 *  se dispararía al montar el compositor, y la selección manual es uno de ocho
 *  tipos. */
export function useBuscarDestinatarios(q: string, activa: boolean) {
  return useQuery({
    queryKey: claves.destinatarios(q.trim()),
    queryFn: () =>
      api.get<PersonaBuscada[]>('/notifications/recipients/search', {
        q: q.trim(), limit: 30,
      }),
    enabled: activa,
    placeholderData: (previa) => previa,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

/** Cuenta los destinatarios sin enviar nada.
 *
 *  Es un `POST` que no escribe —así lo declara el router: «Cuenta y muestra una
 *  muestra de los destinatarios SIN enviar nada»—, por eso es una mutación y no
 *  una consulta: se dispara cuando alguien lo pide, no cuando cambia el
 *  formulario. */
export function usePrevisualizarAudiencia() {
  return useMutation({
    mutationFn: (spec: { audience_type: TipoDeAudiencia; audience_params: ParametrosDeAudiencia }) =>
      api.post<MuestraDeAudiencia>('/communications/audience/preview', spec),
  });
}

export function useEnviar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeEnvio) =>
      api.post<RespuestaDeEnvio>('/communications/send', cuerpo),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.historial }),
  });
}

/** «Enviar ahora» una que estaba en borrador o programada. */
export function useDespachar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<RespuestaDeEnvio>(`/communications/${id}/dispatch`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.historial }),
  });
}
